import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Link } from "react-router";
import { bomApi, type BomAdminListItem } from "@/lib/bom";
import { ListPage } from "@/archetypes/ListPage";
import { DataGrid, type DataGridColumn } from "@/components/data-grid";
import { DetailModal } from "@/components/detail-modal";
import { IntentButton } from "@/components/intent-button";
import { BomEventsTimeline } from "@/components/bom/BomEventsTimeline";

const STATUSES = ["Draft", "Locked", "Final"];

export function BomsAdminPage() {
  const [page, setPage] = useState(0);
  const [q, setQ] = useState("");
  const [status, setStatus] = useState<string>("");
  const [selected, setSelected] = useState<BomAdminListItem | null>(null);

  const { data, isLoading } = useQuery({
    queryKey: ["boms", "admin", page, q, status],
    queryFn: () => bomApi.listAll({ page, q, status: status || undefined }),
  });

  const events = useQuery({
    queryKey: ["boms", "events", selected?.bomId],
    queryFn: () => bomApi.events(selected!.bomId),
    enabled: selected != null,
  });

  const cols: DataGridColumn<BomAdminListItem>[] = [
    { id: "bomId", header: "BOM", sortable: true, accessor: (r) => r.bomId },
    { id: "product", header: "Product", accessor: (r) => r.displayProductName ?? "—" },
    { id: "status", header: "Status", accessor: (r) => r.lifecycleStatus },
    { id: "lines", header: "Line items", accessor: (r) => r.lineItemCount },
    {
      id: "parent",
      header: "Linked to",
      accessor: (r) =>
        r.parentType && r.parentId != null ? `${r.parentType} #${r.parentId}` : "—",
    },
    {
      id: "actions",
      header: "",
      accessor: (r) => (
        <IntentButton intent="ghost" aria-label={`View BOM #${r.bomId}`} onClick={() => setSelected(r)}>View</IntentButton>
      ),
    },
  ];

  return (
    <ListPage backTo="/admin"
      title="All BOMs"
      search={{ value: q, onChange: (v) => { setQ(v); setPage(0); }, placeholder: "Search product…" }}
      filterBar={
        <select
          aria-label="Status filter"
          className="rounded-md border border-input bg-background px-3 py-2 text-sm"
          value={status}
          onChange={(e) => { setStatus(e.target.value); setPage(0); }}
        >
          <option value="">All statuses</option>
          {STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
        </select>
      }
    >
      <DataGrid
        rows={data?.items ?? []}
        columns={cols}
        rowKey={(r) => r.bomId}
        totalRows={data?.total ?? 0}
        pageIndex={page}
        pageSize={50}
        loading={isLoading}
        onPageChange={setPage}
      />

      <DetailModal
        open={selected !== null}
        onOpenChange={(o) => { if (!o) setSelected(null); }}
        title={selected ? `BOM #${selected.bomId}` : ""}
      >
        {selected && (
          <div className="space-y-4">
            <dl className="grid grid-cols-2 gap-x-4 gap-y-2 text-sm">
              <dt className="text-muted-foreground">Product</dt>
              <dd>{selected.displayProductName ?? "—"}</dd>
              <dt className="text-muted-foreground">Status</dt>
              <dd>{selected.lifecycleStatus}</dd>
              <dt className="text-muted-foreground">Line items</dt>
              <dd>{selected.lineItemCount}</dd>
              <dt className="text-muted-foreground">Linked to</dt>
              <dd>
                {selected.parentType && selected.parentId != null ? (
                  <Link
                    className="text-primary underline"
                    to={selected.parentType === "Job" ? `/jobs/${selected.parentId}` : `/quotes/${selected.parentId}`}
                  >
                    {selected.parentType} #{selected.parentId}
                  </Link>
                ) : "Unlinked"}
              </dd>
            </dl>

            <div>
              <h3 className="mb-2 text-sm font-semibold">Events</h3>
              {events.isLoading ? (
                <p className="text-sm text-muted-foreground">Loading…</p>
              ) : (
                <BomEventsTimeline events={events.data?.items ?? []} />
              )}
            </div>
          </div>
        )}
      </DetailModal>
    </ListPage>
  );
}
